/**
 * Asset 360 / Site 360 (tracker 39). Lives under assets:report360. The report's
 * own sentences arrive from the server as keys plus values
 * (supabase/functions/_shared/report360/document.ts); `ui` is the page around it.
 */
export const report360 = {
  ui: {
    finding: "Finding",
    versionPrepared: "Version {{version}} · prepared {{date}}",
    kindAsset: 'Asset 360',
    kindSite: 'Site 360',
    kindVersion: '{{kind}} · version {{version}}',
    preparedFor: 'Prepared for {{name}}',
    writtenFor: 'Written for: {{reader}}',
    prepared: 'Prepared {{date}}',
    figuresAsOf: 'figures as of {{date}}',
    wordsByAi: 'Explanations written by AI (Google Gemini) from the app\'s figures. The AI never changes a number.',
    wordsByApp: 'Written by the app from your records.',
    verdictTitle: 'Verdict',
    contents: 'Contents',
    contentsLine: '{{number}}. {{title}}',
    contentsTail: 'Then: action plan, data quality, about this report.',
    summaryTitle: 'Executive summary',
    keyFindings: 'Key findings',
    doFirst: 'What to do first',
    bullet: '• {{text}}',
    scorecardTitle: 'Scorecard',
    noData: 'No data yet',
    change30d: '{{value}} in 30 days',
    sectionTitle: '{{number}}. {{title}}',
    whatItMeans: 'What this means',
    actionsTitle: 'Action plan',
    actionsNone: 'Nothing needs doing right now.',
    priority1: 'Do now',
    priority2: 'Next',
    priority3: 'When you can',
    due_one: 'Within {{count}} day',
    due_other: 'Within {{count}} days',
    raiseJob: 'Raise a job',
    raiseInspection: 'Start an inspection',
    open: 'Open',
    dataQualityTitle: 'Data quality',
    dataQualityIntro: 'How far this report can rely on your records. Gaps lower the confidence of each section.',
    cannotJudge: 'What the report could not judge',
    getMoreTitle: 'Get more from this report',
    getMoreIntro: 'Only you see this. It is never in what you share.',
    aboutTitle: 'About this report',
    coverage: '{{recorded}} of {{total}} kinds of record are in place.',
    coverageMissing: 'Not recorded yet: {{list}}.',
    aboutAsset_one: 'Scores come from the nightly figures; work and inspections cover the last {{count}} day.',
    aboutAsset_other: 'Scores come from the nightly figures; work and inspections cover the last {{count}} days.',
    aboutSite_one: 'Scores come from the site\'s overnight summary; work covers the last {{count}} day.',
    aboutSite_other: 'Scores come from the site\'s overnight summary; work covers the last {{count}} days.',
    aboutAi: 'The AI wrote the summary and the explanations for the chosen reader. Every number, rating and finding is the app\'s.',
    highRisk: 'high risk',
    nothingToShow: 'Nothing to show yet.',
    labelValue: '{{label}}: {{value}}',
    whereItSits: 'Where it sits',
    dependsOn: 'Depends on',
    serves: 'Serves',
    none: 'None recorded',
    singlePoints: 'Single point of failure: {{names}}',
    hiddenLinks_one: '{{count}} more link you cannot open',
    hiddenLinks_other: '{{count}} more links you cannot open',
    prepareTitle: 'Prepare the report',
    readerLabel: 'Who is it for?',
    readerA11y: 'Write the report for: {{reader}}',
    hideNames: 'Hide contractor names',
    hideNamesHint: 'Shows contractors as Contractor A, B and so on.',
    preparedForLabel: 'Prepared for (optional)',
    preparedForPlaceholder: 'For example, Harbour Property Ltd',
    storyTitle: 'AI version',
    storyHint: 'Google Gemini rewrites the summary and every explanation for your reader, in the language you pick. It never changes a number.',
    languageLabel: 'Language',
    focusLabel: 'Focus the AI on',
    pickFocus: 'Pick at least one section for the AI to focus on.',
    weakData: 'Data quality is weak ({{score}} out of 100). The AI will say what it cannot judge rather than guess.',
    generate: 'Write the AI version',
    chargedWhenWritten: 'The credit is taken only when the report is written. If it fails, nothing is charged.',
    queuedTitle: 'Your AI version is queued',
    queuedBody: 'Number {{position}} in the queue. You can leave this page: you will get a notification when it is ready.',
    runningTitle: 'Writing your AI version…',
    runningBody: 'This usually takes under a minute. You can leave this page: you will get a notification when it is ready.',
    failedTitle: 'The AI version could not be written',
    failedBody: 'Nothing was charged. Try again in a moment.',
    joined: 'Someone already asked for this report, so you will both get the same one.',
    showingAi: 'Showing AI version {{version}}, written {{date}}',
    showingApp: 'Showing the app\'s version for this reader',
    showApp: 'Show the app\'s version',
    showAi: 'Show the AI version',
    loading: 'Building the report…',
    loadFailed: 'The report could not be opened.',
    busy: 'Reports are busy right now. Try again in a moment.',
    retry: 'Try again',
    requestFailedTitle: 'Couldn\'t ask for the AI version',
    earlierTitle: "Before Asset 360",
    earlierHint: "AI reports written before Asset 360 and Site 360. Tap a date to read it.",
    shareTitle: '{{kind}}: {{name}}',
  },
  reader: {
    asset_manager: 'Asset manager',
    client: 'Client',
    landlord: 'Landlord',
    site_manager: 'Site manager',
    investor: 'Investor',
  },
  readerHint: {
    asset_manager: 'The full picture, from its surroundings down to the work, with every section.',
    client: 'Work and contractors first: what was done, how well and on time.',
    landlord: 'Compliance and risk first. Contractor names are hidden unless you switch them on.',
    site_manager: 'Work and condition first, with the action plan straight after the summary.',
    investor: 'About two pages: verdict, scorecard, risk, compliance and the top actions.',
  },
  rating: {
    on_track: 'On track',
    watch: 'Watch',
    needs_attention: 'Needs attention',
  },
  confidence: {
    high: 'Confidence: high',
    medium: 'Confidence: medium',
    low: 'Confidence: low',
  },
  dqBand: {
    strong: 'Strong',
    fair: 'Fair',
    weak: 'Weak',
  },
  dimension: {
    complete: 'Complete',
    current: 'Up to date',
    evidenced: 'Evidenced',
    connected: 'Connected',
    consistent: 'Consistent',
  },
  scorecard: {
    asset: {
      compliance: 'Compliance',
      risk: 'Risk',
      condition: 'Condition',
      inspections_on_time: 'Inspections on time',
      right_first_time: 'Right first time',
      overdue_work: 'Overdue work',
    },
    site: {
      compliance: 'Compliance',
      risk: 'Average risk',
      condition: 'Health',
      inspections_on_time: 'Assets inspected this year',
      right_first_time: 'Right first time',
      overdue_work: 'Overdue work',
    },
  },
  section: {
    environment: 'Where it sits and what is around it',
    compliance: 'Compliance',
    risk: 'Risk',
    condition: 'Condition and inspections',
    work: 'Work',
    contractors: 'Contractors',
    property: 'The property',
    areas: 'Buildings and areas',
    inspections: 'Inspections',
    siteRisk: 'Risk across the site',
  },
  finding: {
    environment: {
      noSite: 'It is not placed on a site, so its surroundings are not known.',
      site: 'It sits on {{site}}, where {{atRisk}} of {{assets}} assets are at risk and {{overdue}} jobs are overdue.',
      siteNoSummary: 'It sits on {{site}}, which has no overnight summary yet.',
    },
    compliance: {
      expired_one: 'One certificate or document has expired.',
      expired_other: '{{count}} certificates or documents have expired.',
      expiring_one: 'One certificate expires in the next 60 days.',
      expiring_other: '{{count}} certificates expire in the next 60 days.',
      noDocuments: 'Compliance stands at {{score}}, but no certificates are on file to back it.',
      noFigures: 'No compliance figures are stored yet.',
      ok_one: 'Compliance stands at {{score}}, with {{count}} document on file and nothing expired.',
      ok_other: 'Compliance stands at {{score}}, with {{count}} documents on file and nothing expired.',
      site: 'Compliance across the site stands at {{score}}.',
      siteExpired_one: 'One document has expired across the site.',
      siteExpired_other: '{{count}} documents have expired across the site.',
    },
    condition: {
      health: 'Health is {{score}} ({{band}}). Days since the last inspection: {{days}}.',
      inspected: 'No health score is stored yet. Days since the last inspection: {{days}}.',
      neverInspected: 'It has never been inspected, so its present condition is unknown.',
      overdueChecks_one: 'One planned check is overdue.',
      overdueChecks_other: '{{count}} planned checks are overdue.',
    },
    contractors: {
      none: 'No contractor has done work here in this period.',
      summary_one: 'One contractor did {{jobs}} jobs: {{busiest}}.',
      summary_other: '{{count}} contractors did {{jobs}} jobs. The busiest was {{busiest}}.',
    },
    inspections: {
      noSummary: 'No inspection summary is stored for this site yet.',
      site: '{{inspected}} of {{assets}} assets were inspected this year. {{failed}} of {{inspections}} inspections in the last 90 days found problems.',
    },
    property: {
      noSummary: 'The site holds {{assets}} assets. No overnight summary is stored yet.',
      summary: 'The site holds {{assets}} assets. {{atRisk}} are at risk and {{open}} jobs are open.',
    },
    risk: {
      level: 'Risk is {{level}} ({{score}} out of 25).',
      neighbours_one: 'Risk is {{level}} ({{score}} out of 25), and one linked asset is high risk.',
      neighbours_other: 'Risk is {{level}} ({{score}} out of 25), and {{count}} linked assets are high risk.',
      notAssessed: 'No risk assessment has been run.',
      singlePoint: 'Risk is {{level}} ({{score}} out of 25), and {{name}} is a single point of failure.',
      site: 'Average risk is {{level}} ({{score}} out of 25). {{atRisk}} assets are at risk.',
      siteNotAssessed: 'The site\'s assets have not been risk assessed.',
    },
    work: {
      none: 'No jobs were raised in the last 12 months.',
      ok: '{{done}} jobs were done in the last 12 months, none overdue and none sent back.',
      overdue_one: 'One job is overdue. {{done}} jobs were done in the last 12 months.',
      overdue_other: '{{count}} jobs are overdue. {{done}} jobs were done in the last 12 months.',
      rework_one: 'One of {{done}} finished jobs was sent back ({{pct}}% right first time).',
      rework_other: '{{count}} of {{done}} finished jobs were sent back ({{pct}}% right first time).',
      siteNone: 'No jobs were raised on the site\'s assets in the last 90 days.',
      siteOk: '{{done}} jobs were done in the last 90 days and none were sent back.',
      siteRework_one: 'One of {{done}} finished jobs was sent back in the last 90 days ({{pct}}% right first time).',
      siteRework_other: '{{count}} of {{done}} finished jobs were sent back in the last 90 days ({{pct}}% right first time).',
    },
    areas: {
      none: 'No buildings or areas are recorded inside the site. Sites inside it: {{n}}.',
      summary_one: 'One building or area is recorded; {{top}} holds {{assets}} assets.',
      summary_other: '{{count}} buildings and areas are recorded. The largest, {{top}}, holds {{assets}} assets.',
    },
  },
  meaning: {
    environment: {
      noSite: 'Without a site, the report cannot say what surrounds it or what a problem nearby would mean for it.',
      ok: 'Its surroundings are in good order, so a problem here is unlikely to be made worse by what is around it.',
      siteWeak: 'The site around it has open problems. Work nearby may disrupt it, and its own issues may matter more.',
      neighboursRisk_one: 'One asset it is linked to is high risk, so a failure there could reach this asset.',
      neighboursRisk_other: '{{count}} assets it is linked to are high risk, so a failure there could reach this asset.',
    },
    compliance: {
      on_track: 'Its paperwork is in order. Keep renewing on time and this stays clear.',
      watch: 'Compliance is slipping. Renew what is due before it becomes a finding.',
      needs_attention: 'Compliance needs attention now: an inspector or insurer would raise this.',
      none: 'There are no compliance figures to judge by yet.',
      expired: 'An expired certificate means it may not be legal or insurable to use until it is renewed.',
      noDocuments: 'With nothing on file, the score cannot be proved to anyone who asks.',
      awaitingCheck_one: 'One document is waiting to be checked, so it does not count yet.',
      awaitingCheck_other: '{{count}} documents are waiting to be checked, so they do not count yet.',
    },
    risk: {
      on_track: 'Risk is low. Normal care and planned checks are enough.',
      watch: 'Risk is moderate. Keep checks on time and watch for change.',
      needs_attention: 'Risk is high. A failure would be costly or unsafe, so act on the plan below.',
      notAssessed: 'Without a risk assessment, nobody can say how much a failure would matter.',
      noLinks: 'Risk is {{level}}. No links to other assets are recorded, so knock-on effects are unknown.',
      singlePoint: 'Other assets depend on it with no backup, so a failure here stops them too.',
    },
    condition: {
      on_track: 'It is in good condition and inspected on time.',
      watch: 'Its condition is starting to slip. Keep an eye on the next inspection.',
      needs_attention: 'Its condition is poor. Plan repair or replacement.',
      none: 'There is no stored health score to judge its condition by yet.',
      neverInspected: 'Nobody has looked at it in the app, so its condition is a guess until it is inspected.',
      issues_one: 'One recent inspection found problems that need following up.',
      issues_other: '{{count}} recent inspections found problems that need following up.',
      repeat: '"{{item}}" keeps coming back. Fixing the cause would save repeat visits.',
    },
    work: {
      none: 'No work means either nothing was needed or work is not being recorded in the app.',
      ok: 'Work is done on time and right first time.',
      overdue: 'Overdue jobs leave known problems unfixed and raise the risk.',
      rework: 'Jobs sent back cost a second visit and delay the fix.',
    },
    contractors: {
      none: 'No contractor work is recorded, so their performance cannot be judged.',
      good: 'Contractors finish on time and get it right first time.',
      late_one: 'One contractor often finishes late.',
      late_other: '{{count}} contractors often finish late.',
      sentBack_one: 'One contractor has had work sent back.',
      sentBack_other: '{{count}} contractors have had work sent back.',
      offApp_one: 'One contractor does not use the app, so their work has less evidence.',
      offApp_other: '{{count}} contractors do not use the app, so their work has less evidence.',
    },
    property: {
      on_track: 'The property is in good order overall.',
      watch: 'The property is mostly in order, with some areas to watch.',
      needs_attention: 'The property needs attention: compliance or condition is below where it should be.',
      truncated: 'This site holds {{n}} assets; the report reads the first 5,000.',
    },
    areas: {
      none: 'Without buildings or areas, the report cannot show where problems are concentrated.',
      ok: 'No building or area stands out as a problem.',
      highRisk: 'High-risk assets are concentrated in {{area}}. Start there.',
    },
    inspections: {
      on_track: 'Nearly every asset was inspected this year.',
      watch: 'Some assets have not been inspected this year.',
      needs_attention: 'Many assets have not been inspected this year, so their condition is unknown.',
      none: 'There is no inspection summary to judge by yet.',
      overdue: 'Planned checks are overdue. Catch up before problems are missed.',
    },
  },
  basis: {
    environment: {
      summary: 'From the site\'s overnight summary of {{date}}.',
      noSummary: 'From the site record; the site has no overnight summary yet.',
    },
    site: {
      summary: 'From the overnight summary of {{date}}.',
    },
    compliance: 'From the nightly figures and documents on file ({{n}}).',
    risk: 'From the latest risk assessment and links to other assets ({{links}}).',
    condition: 'From the nightly health figures and recent inspections ({{n}}).',
    work: {
      year: 'From the jobs of the last 12 months ({{n}}).',
      ninetyDays: 'From the jobs of the last 90 days ({{n}}).',
    },
    contractors: {
      year: 'From contractor jobs of the last 12 months ({{n}}).',
      ninetyDays: 'From contractor jobs of the last 90 days ({{n}}).',
    },
    areas: 'From the places recorded on the site and the assets in them ({{n}}).',
    siteRisk: 'From the risk assessments of the site\'s assets ({{n}}).',
  },
  score: {
    compliance: {
      on_track: 'Clear: paperwork in order.',
      watch: 'Some paperwork due or missing.',
      needs_attention: 'Paperwork expired or missing.',
      none: 'No figures yet.',
    },
    risk: {
      on_track: 'Low risk.',
      watch: 'Moderate risk.',
      needs_attention: 'High risk.',
      none: 'Not assessed.',
    },
    condition: {
      on_track: 'Good condition.',
      watch: 'Condition slipping.',
      needs_attention: 'Poor condition.',
      none: 'No health figures yet.',
    },
    inspectionsOnTime: {
      on_track: 'Planned checks done on time.',
      watch: 'Some checks late.',
      needs_attention: 'Many checks late.',
      none: 'No planned checks.',
    },
    inspectedThisYear: {
      on_track: 'Nearly every asset inspected this year.',
      watch: 'Some assets not inspected this year.',
      needs_attention: 'Many assets not inspected this year.',
      none: 'No inspection summary yet.',
    },
    rightFirstTime: {
      on_track: 'Work done right first time.',
      watch: 'Some work sent back.',
      needs_attention: 'Much work sent back.',
      none: 'No finished work to judge.',
    },
    overdueWork: {
      on_track: 'Nothing overdue.',
      watch: 'Jobs past their due date: {{n}}.',
      needs_attention: 'Jobs past their due date: {{n}}.',
      none: 'No work recorded.',
    },
  },
  band: {
    posture: {
      on_track: 'Clear',
      watch: 'Watch',
      needs_attention: 'Action',
    },
    risk: {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      extreme: 'Extreme',
    },
  },
  verdict: {
    on_track: 'On track: {{reason}}',
    watch: 'Worth watching: {{reason}}',
    needs_attention: 'Needs attention: {{reason}}',
    on_track_all: 'On track: every measure is in good order.',
  },
  reason: {
    compliance: 'compliance is at {{value}}.',
    risk: 'risk is at {{value}} out of 25.',
    condition: 'health is at {{value}}.',
    inspections_on_time: '{{value}}% of inspections are on time.',
    right_first_time: '{{value}}% of work is right first time.',
    overdue_work: '{{value}} jobs are past their due date.',
  },
  stat: {
    jobsWithSubJobs: "Jobs with sub-jobs",
    familiesOnTime: "Of those, on time",
    waitBetweenSteps: "Wait between steps",
    stepsSentBack: "Steps sent back",
    hoursValue: "{{n}} h",
    assets: 'Assets',
    assetsAtRisk: 'Assets at risk',
    checksDone: 'Checks done',
    checksNext60: 'Checks due in 60 days',
    checksOverdue: 'Checks overdue',
    checksPlanned: 'Checks planned',
    expiredDocuments: 'Expired documents',
    failed90d: 'Failed inspections (90 days)',
    inspectedThisYear: 'Inspected this year',
    inspections90d: 'Inspections (90 days)',
    jobsDone: 'Jobs done',
    links: 'Linked assets',
    neighbours: 'Assets nearby',
    onTime: 'On time',
    openJobs: 'Open jobs',
    overdueJobs: 'Overdue jobs',
    rejected: 'Rejected',
    rescheduled: 'Rescheduled',
    rightFirstTime: 'Right first time',
    sentBack: 'Sent back',
    siteAtRisk: 'Site assets at risk',
    siteCompliance: 'Site compliance',
    siteHealth: 'Site health',
    siteOverdueJobs: 'Site overdue jobs',
  },
  col: {
    area: 'Area',
    asset: 'Asset',
    assets: 'Assets',
    avgDays: 'Average days',
    contractor: 'Contractor',
    date: 'Date',
    document: 'Document',
    expires: 'Expires',
    highRisk: 'High risk',
    issues: 'Issues',
    job: 'Job',
    jobs: 'Jobs',
    kind: 'Kind',
    level: 'Level',
    onTime: 'On time',
    reason: 'Reason',
    result: 'Result',
    risk: 'Risk',
    sentBack: 'Sent back',
    site: 'Site',
    status: 'Status',
    usesApp: 'Uses the app',
  },
  visual: {
    areas: 'Buildings and areas',
    byZone: 'Assets by zone',
    childSites: 'Sites inside this site',
    complianceTrend: 'Compliance, last 90 days',
    contractors: 'Contractors',
    documents: 'Documents',
    healthTrend: 'Health, last 90 days',
    openJobsTrend: 'Open jobs, last 90 days',
    recentInspections: 'Recent inspections',
    repeatItems: 'Faults that keep coming back',
    rework: 'Why work was sent back',
    riskTrend: 'Risk, last 90 days',
    topRisk: 'Highest-risk assets',
  },
  docStatus: {
    awaitingCheck: 'Awaiting check',
    expired: 'Expired',
    expiring: 'Expires soon',
    valid: 'Valid',
  },
  result: {
    issues: 'Issues found',
    passed: 'Passed',
    open: 'Not finished',
  },
  common: {
    yes: 'Yes',
    no: 'No',
    noReason: 'No reason given',
    noExpiry: 'No expiry',
  },
  contractors: {
    anonymous: 'Contractor {{letter}}',
  },
  action: {
    planReplacement: "Plan the replacement of {{name}}",
    backup: 'Plan a backup for {{name}}',
    clearOverdue_one: 'Clear the overdue job',
    clearOverdue_other: 'Clear the {{count}} overdue jobs',
    fillDetails: 'Fill in the missing details',
    fillSiteDetails: 'Fill in the site\'s missing details',
    inspect: 'Inspect {{name}}',
    inspectRest_one: 'Inspect the one asset not inspected this year',
    inspectRest_other: 'Inspect the {{count}} assets not inspected this year',
    overdueChecks_one: 'Carry out the overdue check',
    overdueChecks_other: 'Carry out the {{count}} overdue checks',
    renewExpired_one: 'Renew the expired certificate',
    renewExpired_other: 'Renew the {{count}} expired certificates',
    renewExpiring_one: 'Renew the certificate that expires soon',
    renewExpiring_other: 'Renew the {{count}} certificates that expire soon',
    repeatFault: 'Find the cause of "{{item}}"',
    reviewRisk: 'Review the risk of {{name}}',
    rework: 'Talk to the contractors about work sent back',
  },
  why: {
    pastLife: "It is past its expected life.",
    spendHigh: "A year of repairs has cost half or more of a new one.",
    backup: 'Other assets depend on it with no backup.',
    clearOverdue: 'Overdue jobs leave known problems unfixed.',
    fillDetails: 'Data quality is {{score}} out of 100, which limits what the report can say.',
    neverInspected: 'Its condition is unknown until someone looks at it.',
    overdueChecks: 'Planned checks are past their date.',
    renewExpired: 'An expired certificate can make it unlawful or uninsurable to use.',
    renewExpiring: 'Renewing before the expiry date avoids a gap in cover.',
    repeatFault_one: 'It needed a job once already.',
    repeatFault_other: 'It has needed a job {{count}} times.',
    reviewRisk: 'Its risk is {{level}}.',
    rework_one: 'One job was sent back.',
    rework_other: '{{count}} jobs were sent back.',
  },
  jobTitle: {
    planReplacement: "Plan replacement",
    findCause: 'Find the cause of the repeat fault: {{item}}',
    renewCertificates: 'Renew expired certificates',
    riskReview: 'Risk review',
  },
  getMore: {
    addValue: "Add when it was installed, how long it should last and what replacing it costs, so the report can show its life and value.",
    addValueButton: "Add value and life",
    addDetails: 'Fill in the missing details to raise data quality and the confidence of every section.',
    addDetailsButton: 'Edit the asset',
    addSiteDetails: 'Fill in the site\'s missing details to raise data quality.',
    addSiteDetailsButton: 'Edit the site',
    groupRule: 'Set a group rule so assets like this get the right checks automatically.',
    groupRuleButton: 'Open group rules',
    inviteContractor: 'Invite your contractors so their work is recorded with evidence.',
    inviteContractorButton: 'Invite a contractor',
    linkAssets: 'Link it to the assets it depends on or serves, so knock-on risk shows.',
    linkAssetsButton: 'Add links',
    placeAssets: 'Put the site\'s assets into buildings and areas so problems show by place.',
    placeAssetsButton: 'Open the site',
    placeOnSite: 'Place it on a site so the report can describe its surroundings.',
    placeOnSiteButton: 'Choose a site',
    scheduleInspection: 'Schedule an inspection so its condition is known.',
    scheduleInspectionButton: 'Schedule an inspection',
    siteAddress: 'Add the site\'s address or map pin.',
    siteAddressButton: 'Add the address',
    uploadCertificate: 'Upload its certificates so compliance can be proved.',
    uploadCertificateButton: 'Upload a document',
  },
  coverage: {
    asset: {
      site: 'a site',
      place: 'where it sits',
      links: 'links to other assets',
      documents: 'documents',
      inspection: 'an inspection this year',
      risk: 'a risk assessment',
      criticality: 'criticality',
      figures: 'nightly figures',
      work: 'work history',
      schedule: 'planned checks',
    },
    site: {
      address: 'an address or map pin',
      summary: 'an overnight summary',
      areas: 'buildings and areas',
      inspected: 'inspections for nearly every asset',
      assessed: 'risk assessments for nearly every asset',
      evidence: 'evidence for nearly every asset',
      contractors: 'contractor work',
      located: 'map locations for the assets',
    },
  },
  gaps: {
    missingField: 'No {{field}} recorded.',
    neverInspected: 'Never inspected, so its present condition is unknown.',
    inspectedLongAgo: 'Last inspected {{days}} days ago, so its condition may have changed.',
    noInspectionThisYear: 'No inspection completed in the last 12 months.',
    noDocuments: 'No documents (certificates, manuals, warranties) on file.',
    noPhotos: 'No photos.',
    noRiskAssessment: 'No risk assessment has been run.',
    noSite: 'Not placed on a site, so its surroundings cannot be described.',
    noLocation: 'No map location.',
    noLinks: 'Not linked to any related asset, so knock-on effects are unknown.',
    retiredWithOpenJobs_one: 'Marked {{status}} but still has {{count}} open job.',
    retiredWithOpenJobs_other: 'Marked {{status}} but still has {{count}} open jobs.',
    riskWithoutCriticality: 'Has a risk score but no criticality, so the score rests on a default.',
    zonesWithoutSite: 'Has zones but no site, so the zones have nothing to belong to.',
    noStoredFigures: 'No stored daily figures yet, so trends cannot be shown.',
    staleFigures: 'The stored daily figures are {{days}} days old.',
    missingFieldOnAssets_one: 'No {{field}} on {{count}} of {{total}} assets.',
    missingFieldOnAssets_other: 'No {{field}} on {{count}} of {{total}} assets.',
    noAssets: 'No assets on this site.',
    assetsNotInspected_one: '{{count}} of {{total}} assets has not been inspected in the last 12 months.',
    assetsNotInspected_other: '{{count}} of {{total}} assets have not been inspected in the last 12 months.',
    noInspectionHistory: 'No inspection history is summarised for this site yet.',
    assetsWithNothing_one: 'One asset has nothing recorded at all.',
    assetsWithNothing_other: '{{count}} assets have nothing recorded at all.',
    assetsNotAssessed_one: 'One asset has no risk assessment.',
    assetsNotAssessed_other: '{{count}} assets have no risk assessment.',
    siteNotLocated: 'The site has no address or map location.',
    assetsNotLocated_one: '{{count}} of {{total}} assets has no map location.',
    assetsNotLocated_other: '{{count}} of {{total}} assets have no map location.',
    noSiteSummary: 'No stored site summary yet, so trends cannot be shown.',
    staleSiteSummary: 'The stored site summary is {{days}} days old.',
    assetCountMoved_one: 'The site now has {{count}} asset, but last night\'s summary counted {{summarised}}.',
    assetCountMoved_other: 'The site now has {{count}} assets, but last night\'s summary counted {{summarised}}.',
    assetsRiskWithoutCriticality_one: 'One asset has a risk score but no criticality.',
    assetsRiskWithoutCriticality_other: '{{count}} assets have a risk score but no criticality.',
  },
  versions: {
    title: "Versions",
    hint: "Each saved version stays exactly as it was. Open one to read it or compare it with the one before.",
    none: "No saved versions yet. Save this version, or write the AI version, to start the list.",
    save: "Save this version",
    saveFailed: "Could not save the version",
    savedTitle: "Saved",
    savedBody: "Saved as Version {{version}}.",
    label: "Version {{version}} · {{date}}",
    ai: "Written by AI",
    app: "The app's figures",
    open: "Open",
    close: "Close",
    viewing: "You are reading Version {{version}}, saved {{date}}.",
    backToCurrent: "Back to the current report",
  },
  compare: {
    title: "What changed from Version {{older}} to Version {{newer}}",
    show: "Compare with Version {{version}}",
    hide: "Hide the comparison",
    verdict: "Overall: {{before}} → {{after}} ({{movement}})",
    dataQuality: "Data quality: {{before}} → {{after}} ({{movement}})",
    figure: "{{before}} → {{after}} ({{movement}})",
    actionsAdded: "New actions: {{n}}",
    actionsGone: "Actions no longer needed: {{n}}",
    bullet: "• {{text}}",
    movement: {
      better: "better",
      worse: "worse",
      same: "no change",
      new: "new",
      gone: "no longer shown",
    },
  },
  share: {
    open: "Share",
    title: "Share this report",
    hint: "They are told in the app and by e-mail. The link opens the report in AssetWRX: the cover and scorecard at once, the rest after they sign in or create a free account.",
    toLabel: "Send to",
    toPlaceholder: "Search contacts or type an e-mail address",
    messageLabel: 'Message (optional)',
    messagePlaceholder: 'Add a note for the recipient...',
    savesFirst: "What is on screen is saved as a new version first, so they read exactly this.",
    send: "Send report",
    sentTitle: "Shared",
    sentBody: "Version {{version}} was shared with {{who}}.",
    failedTitle: "Could not share",
    failedBody: "Something went wrong. Try again.",
    sharedWith: "Shared with",
    versionOn: "Version {{version}} · {{date}}",
    read: "Read {{date}}",
    notRead: "Not read yet",
    withdrawn: "Withdrawn",
    replaced: "Replaced by a newer version",
    sendNew: "Send Version {{version}}",
    withdraw: "Withdraw",
    close: "Close",
  },
  download: {
    pdf: "Download PDF",
    csv: "Download CSV",
    approverOnly: "Downloads are for whoever approves work on this asset. Use Share to send it to someone.",
  },
  csv: {
    similarMiddle: "Middle of similar",
    basis: "Based on",
    part: "Part",
    item: "Item",
    value: "Value",
    band: "Rating",
    note: "Note",
    subject: "Subject",
    version: "Version",
    written: "Written",
    reader: "Written for",
  },
  shared: {
    header: "Shared report",
    loadFailed: "The report could not be loaded.",
    notFound: "This link does not lead to a report.",
    goToApp: "Go to AssetWRX",
    revoked: "This report is no longer shared with you.",
    replaced: "A newer version of this report has been shared with you.",
    openNewer: "Open the newer version",
    notYours: "This report was shared with someone else. Sign in with the address it was sent to.",
    verdict: "Overall: {{rating}}",
    gateTitle: "Read the whole report",
    gateBody: "Sign in, or create a free account, to read every section, the action plan and the data quality.",
    signIn: "Sign in",
    createAccount: "Create a free account",
    sharedBy: "Shared by {{who}} on {{date}}",
    sharedOn: "Shared on {{date}}",
    note: "Their note: {{message}}",
    newerExists: "There is a newer version (Version {{version}}). Ask the sender if you need it.",
    readOnly: "Read-only. This report stays as it was when it was shared.",
  },
  comparison: {
    title: "How it compares",
    measure: {
      compliance: "Compliance",
      condition: "Condition",
      risk: "Risk",
      right_first_time: "Right first time",
    },
    position: {
      better: "better than most",
      similar: "about the same as most",
      worse: "behind most",
    },
    row: "{{value}}, {{position}} (middle of similar: {{middle}})",
    notEnough: "Too few similar ones have this figure to compare.",
    basis: {
      asset_one: "Compared with 1 similar asset you can see, on last night's figures.",
      asset_other: "Compared with {{count}} similar assets you can see, on last night's figures.",
      site_one: "Compared with 1 other site you can see, on last night's figures.",
      site_other: "Compared with {{count}} other sites you can see, on last night's figures.",
    },
    finding: {
      behind: "Behind most similar ones on {{measure}}.",
      ahead: "Ahead of most similar ones on {{measure}}.",
      inLine: "In line with similar ones.",
    },
  },
  value: {
    title: "Value and life",
    measure: {
      installed_on: "Installed",
      age: "Age",
      expected_life: "Expected life",
      remaining_life: "Life left",
      purchase_cost: "Purchase cost",
      replacement_cost: "Replacement cost",
      spend: "Spent on jobs",
      spend_share: "Spend as a share of replacement cost",
      replacement_total: "Replacement value recorded",
      assets_with_value: "Assets with a replacement cost",
      past_life: "Assets past their expected life",
    },
    years_one: "{{value}} year",
    years_other: "{{value}} years",
    lifeUsed: "{{pct}}% of its expected life used",
    finding: {
      pastLife: "It is {{years}} years past its expected life.",
      spendHigh: "A year of jobs cost {{pct}}% of replacing it. Replacing may cost less than repairing.",
      nearEnd: "It has used {{pct}}% of its expected life.",
      lifeLeft: "About {{years}} years of its expected life are left.",
      recorded: "What is recorded about its value.",
      sitePastLife_one: "1 of {{of}} assets with a recorded life is past it.",
      sitePastLife_other: "{{count}} of {{of}} assets with a recorded life are past it.",
      siteRecorded_one: "1 of {{of}} assets has a replacement cost recorded.",
      siteRecorded_other: "{{count}} of {{of}} assets have a replacement cost recorded.",
      siteSpendOnly: "What the site's jobs cost.",
    },
    spendBasis: {
      year: "Spend over the last 12 months, from {{withCost}} of {{jobs}} finished jobs that carry a cost.",
      ninetyDays: "Spend over the last 90 days, from {{withCost}} of {{jobs}} finished jobs that carry a cost.",
    },
    marketTitle: "Typical market price",
    marketHint: "From online sources, not checked by AssetWRX.",
  },
  // ESG chapter (tracker 44).
  esg: {
    section: "Energy, carbon and sustainability (ESG)",
    basis: "From meter readings over the last 24 months, the refrigerant and energy certificates recorded, and UK government carbon factors ({{year}}). Carbon is an estimate.",
    ratingsTable: "Energy certificates",
    col: {
      certificate: "Certificate",
      rating: "Rating",
    },
    finding: {
      belowMinimum: "Energy certificates below the letting minimum (E): {{n}}.",
      fgasOverdue: "F-gas leak checks overdue: {{n}}.",
      noMeters: "No energy meters are recorded, so energy and carbon cannot be measured.",
      energyChange: "{{kwh}} kWh used in the last 12 months ({{change}}% on the year before), about {{tonnes}} t CO₂e.",
      energy: "{{kwh}} kWh used in the last 12 months, about {{tonnes}} t CO₂e.",
    },
    meaning: {
      belowMinimum: "A let building in England and Wales cannot be let below E without an exemption. Plan the improvement or register an exemption.",
      fgasOverdue: "Overdue leak checks break the F-gas rules and let refrigerant, a strong greenhouse gas, escape unnoticed.",
      noMeters: "Without meters, energy use and carbon are unknown. Add meters and take readings to report them.",
      noFloorArea: "Add the floor area to compare energy use per m² with other buildings.",
      ok: "Energy and carbon are being measured. Keep readings regular so the figures stay complete.",
    },
    stat: {
      energy: "Energy, kWh (12 months)",
      change: "Change on the year, %",
      intensity: "kWh per m²",
      carbon: "Carbon, t CO₂e",
      scope1: "Scope 1, t CO₂e",
      scope2: "Scope 2, t CO₂e",
      water: "Water, m³",
      meters: "Meters reporting",
      refrigerant: "Refrigerant, t CO₂e",
      fgasOverdue: "F-gas checks overdue",
    },
    status: {
      belowMinimum: "Below the letting minimum",
      expired: "Expired",
      expiresSoon: "Expires within 6 months",
      valid: "Valid",
    },
    action: {
      improveEpc: "Plan works to lift the energy rating to E or better",
      fgasOverdue: "Carry out the overdue F-gas leak checks ({{n}})",
      renewEpc: "Renew the expired energy certificate",
      readMeters: "Take readings on the meters that have none for 3 months ({{n}})",
    },
    why: {
      improveEpc: "Below E it cannot legally be let without an exemption.",
      fgasOverdue: "Leak checks are a legal duty at this refrigerant size.",
      renewEpc: "An energy certificate is needed to sell or let.",
      readMeters: "Gaps in readings make the energy and carbon figures incomplete.",
    },
    jobTitle: {
      improveEpc: "Energy rating improvement works",
      renewEpc: "Renew energy performance certificate",
    },
  },
} as const;
